import type { PieceData } from '../types';
import { CHORALE_FILES } from './catalog.generated';
import { decodeChorale, decodeNotes } from './codec';
import { DerivedChorale, deriveChorale } from './derive';
import type { ChoraleFile, ChoraleJson } from './types';
import { keyToken, parseKey } from '../../music/key';
import type { NoteEvent } from '../../music/piece';
import { midiOf, parsePitch, pitchName, transposePitch } from '../../music/pitch';

/**
 * BWV 244.17 ("Ich will hier bei dir stehen") is not in the Riemenschneider collection because its
 * harmony is identical to BWV 244.15, a semitone lower. We derive it by transposition and say so.
 */
function transposeChorale(c: ChoraleJson, steps: number, semis: number, patch: Partial<ChoraleJson>): ChoraleJson {
  const notes = c.notes.map((n) => {
    const p = transposePitch(parsePitch(n.name), steps, semis);
    const { lyric: _lyric, ...rest } = n;
    return { ...rest, midi: midiOf(p), name: pitchName(p) };
  });
  const moveKey = (key: string) => {
    const k = parseKey(key);
    const t = transposePitch({ ...k.tonic, octave: 4 }, steps, semis);
    return keyToken({ tonic: { letter: t.letter, alter: t.alter }, mode: k.mode });
  };
  const harmony = c.harmony.map((h) => ({
    ...h,
    key: h.key ? moveKey(h.key) : h.key,
    ...(h.pivot ? { pivot: { key: moveKey(h.pivot.key), rn: h.pivot.rn } } : {}),
  }));
  return { ...c, ...patch, notes, harmony };
}

const FILES = new Map<string, ChoraleFile>(CHORALE_FILES.map((f) => [f.id, f]));

/** Derived settings: id → how to make them from a stored one. */
const DERIVED: Record<string, { from: string; make: (base: ChoraleJson) => ChoraleJson }> = {
  'bwv244-17': {
    from: 'bwv244-15',
    make: (base) =>
      transposeChorale(base, 0, -1, {
        id: 'bwv244-17',
        bwv: '244.17',
        stanza: '6',
        words: { kept: false, reason: 'no words encoded' },
        derivedFrom: 'bwv244-15',
      }),
  },
};

/** Every chorale in the atlas: the stored ones in Riemenschneider order, then derived settings. */
export const CHORALE_IDS: string[] = [...CHORALE_FILES.map((f) => f.id), ...Object.keys(DERIVED).filter((id) => FILES.has(DERIVED[id].from))];

const decoded = new Map<string, ChoraleJson>();
export function choraleJson(id: string): ChoraleJson | undefined {
  let c = decoded.get(id);
  if (!c) {
    const file = FILES.get(id);
    if (file) c = decodeChorale(file);
    else if (DERIVED[id]) {
      const base = choraleJson(DERIVED[id].from);
      if (base) c = DERIVED[id].make(base);
    }
    if (c) decoded.set(id, c);
  }
  return c;
}

/** Just the notes, without deriving anything (cheap; used to play excerpts). */
export function choraleNotes(id: string): NoteEvent[] | undefined {
  const file = FILES.get(id);
  if (file && !decoded.has(id)) return decodeNotes(file);
  return choraleJson(id)?.notes;
}

export function choraleFile(id: string): ChoraleFile | undefined {
  return FILES.get(id);
}

const cache = new Map<string, DerivedChorale>();
/** Derived lazily and cached: the derivation is deterministic, so this is only a speed-up. */
export function derivedChorale(id: string): DerivedChorale | undefined {
  let d = cache.get(id);
  if (!d) {
    const json = choraleJson(id);
    if (!json) return undefined;
    d = deriveChorale(json);
    cache.set(id, d);
  }
  return d;
}

export function choralePiece(id: string): PieceData | undefined {
  return derivedChorale(id)?.piece;
}

/** Derives every chorale: slow-ish (a second or two). Build scripts and tests only. */
export function allChorales(): DerivedChorale[] {
  return CHORALE_IDS.map((id) => derivedChorale(id)!);
}
